import { ThemeLogo } from '@mui/material';
import {
  createTheme,
  lighten,
  Theme,
  ThemeOptions,
} from '@mui/material/styles';

import { Branding_BrandedFragment } from '@/modules/admin/branding/graphql/Branding.generated';
import { PresentationHeaderFontFace } from '@/types/schema';

import { romie } from '../fonts/romie';
import { COLORS as BaseColors } from '../tokens/colors';
import {
  COMMON_THEME_PROPERTIES,
  generateDataVisualizationPaletteFromBaseColor,
  generatePaletteFromBaseColor,
  THEME_SHADOWS,
} from './common';

// https://www.figma.com/file/Jv6op8db2Qe3tIpArzQyr4/Dev-Specs?node-id=4148%3A40193
export enum ColorKey {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  BUTTONS_LINKS = 'BUTTONS_LINKS',
  DATA_VISUALIZATION_PRIMARY = 'DATA_VISUALIZATION_PRIMARY',
  DATA_VISUALIZATION_SECONDARY = 'DATA_VISUALIZATION_SECONDARY',
  DATA_VISUALIZATION_TERTIARY = 'DATA_VISUALIZATION_TERTIARY',
  DATA_VISUALIZATION_NEGATIVE = 'DATA_VISUALIZATION_NEGATIVE',
}

/**
 * Header typeface for the branded display variants (`h1_display`/`h2_display`).
 * Defaults to the serif Romie face rather than the base theme's Playfair; a
 * brand may opt into Arial via `presentationHeaderFontFace`.
 */
const HEADER_FONT_FAMILY_BY_FONT_FACE: Record<
  PresentationHeaderFontFace,
  string
> = {
  [PresentationHeaderFontFace.Default]: romie.style.fontFamily,
  [PresentationHeaderFontFace.Arial]: 'Arial, Helvetica, sans-serif',
};

export const BRAND: Record<ColorKey, string> = {
  PRIMARY: '#B91C1C',
  SECONDARY: '#09627E',
  BUTTONS_LINKS: '#374151',
  DATA_VISUALIZATION_PRIMARY: '#0C2F39',
  DATA_VISUALIZATION_SECONDARY: '#A37316',
  DATA_VISUALIZATION_TERTIARY: '#1A6FC9',
  DATA_VISUALIZATION_NEGATIVE: '#FA9F9B',
};

export function dataToBrand(data: Branding_BrandedFragment): {
  brand: typeof BRAND;
  logos: ThemeLogo;
} {
  return {
    brand: {
      PRIMARY: data.primaryColor,
      SECONDARY: data.secondaryColor,
      BUTTONS_LINKS: data.buttonsLinkColor,
      DATA_VISUALIZATION_PRIMARY: data.dataVisualizationPrimaryColor,
      DATA_VISUALIZATION_SECONDARY: data.dataVisualizationSecondaryColor,
      DATA_VISUALIZATION_TERTIARY:
        data.dataVisualizationTertiaryColor ??
        data.dataVisualizationSecondaryColor,
      DATA_VISUALIZATION_NEGATIVE:
        data.dataVisualizationNegativeColor ??
        BRAND.DATA_VISUALIZATION_NEGATIVE,
    },
    logos: {
      primaryWide: data.lightWideLogoURL,
      secondaryWide: data.darkWideLogoURL,
      secondarySquare: data.darkSquareLogoURL,
    },
  };
}

export function themeToBrand(theme: Theme): typeof BRAND {
  return {
    PRIMARY: theme.palette.primary.main,
    SECONDARY: theme.palette.secondary.main,
    BUTTONS_LINKS: theme.palette.links.main,
    DATA_VISUALIZATION_PRIMARY: theme.palette.dataVisualizationPrimary.main,
    DATA_VISUALIZATION_SECONDARY: theme.palette.dataVisualizationSecondary.main,
    DATA_VISUALIZATION_TERTIARY: theme.palette.dataVisualizationTertiary.main,
    DATA_VISUALIZATION_NEGATIVE: theme.palette.dataVisualizationNegative.main,
  };
}

export function makeThemeFromBrand(
  colors: Record<ColorKey, string>,
  logos: ThemeLogo = {},
  presentationHeaderFontFace?: PresentationHeaderFontFace | null
): ThemeOptions {
  // Display headings default to Romie (even when the face is null/unset, so we
  // don't regress to the base Playfair) and switch to Arial when the brand
  // opts in. title-*/body are handled below (forcedFontStyle).
  const displayHeadingFontFamily =
    HEADER_FONT_FAMILY_BY_FONT_FACE[
      presentationHeaderFontFace ?? PresentationHeaderFontFace.Default
    ];
  const displayHeadingStyle = {
    color: BaseColors.NEUTRAL_GRAY[900],
    fontFamily: displayHeadingFontFamily,
  };

  // A non-default (Arial) face applies to ALL text, not just headers. Set
  // per-variant (below) rather than via an !important override so the
  // Lexical/Markdown editor — which reads theme.typography.* — picks it up too.
  const shouldForceBrandFontFace =
    presentationHeaderFontFace != null &&
    presentationHeaderFontFace !== PresentationHeaderFontFace.Default;
  const forcedFontStyle = shouldForceBrandFontFace
    ? { fontFamily: displayHeadingFontFamily }
    : {};

  const {
    PRIMARY,
    SECONDARY,
    BUTTONS_LINKS,
    DATA_VISUALIZATION_PRIMARY,
    DATA_VISUALIZATION_SECONDARY,
    DATA_VISUALIZATION_TERTIARY,
    DATA_VISUALIZATION_NEGATIVE,
  } = colors;

  const primaryPalette = generatePaletteFromBaseColor(PRIMARY);
  const secondaryPalette = generatePaletteFromBaseColor(SECONDARY);
  const linksPalette = generatePaletteFromBaseColor(BUTTONS_LINKS);
  const buttonsPalette = generatePaletteFromBaseColor(BUTTONS_LINKS);
  const dataVisualizationPrimaryPalette =
    generateDataVisualizationPaletteFromBaseColor(DATA_VISUALIZATION_PRIMARY);
  const dataVisualizationSecondaryPalette =
    generateDataVisualizationPaletteFromBaseColor(DATA_VISUALIZATION_SECONDARY);
  const dataVisualizationTertiaryPalette =
    generateDataVisualizationPaletteFromBaseColor(DATA_VISUALIZATION_TERTIARY);
  const dataVisualizationNegativePalette =
    generateDataVisualizationPaletteFromBaseColor(DATA_VISUALIZATION_NEGATIVE);

  const BODY_TEXT_COLOR = BaseColors.PRIMITIVES.BLACK;

  return {
    typography: {
      // Base family for non-variant / inherited text when a non-default face
      // is selected.
      ...forcedFontStyle,
      h1_display: displayHeadingStyle,
      h2_display: displayHeadingStyle,
      'title-serif-m': displayHeadingStyle,
      // title-* / h2light keep their base face (Inter) unless the brand opts
      // into a non-default face; only then do they take the brand face.
      h2light: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'title-xl': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'title-l': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'title-m': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'title-s': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'title-xs': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'body-m': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'body-s': {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      'body-xs': {
        color: BaseColors.NEUTRAL_GRAY[500],
        ...forcedFontStyle,
      },
      'eyebrow-xs': {
        color: BaseColors.NEUTRAL_GRAY[500],
        ...forcedFontStyle,
      },
      h1: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      h2: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      h3: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      h4: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      h5: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      h6: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      subtitle1: {
        color: BaseColors.NEUTRAL_GRAY[500],
        ...forcedFontStyle,
      },
      subtitle2: {
        color: BaseColors.NEUTRAL_GRAY[500],
        ...forcedFontStyle,
      },
      body1: {
        color: BaseColors.NEUTRAL_GRAY[900],
        ...forcedFontStyle,
      },
      body2: {
        color: BaseColors.NEUTRAL_GRAY[600],
        ...forcedFontStyle,
      },
      button: {
        color: BODY_TEXT_COLOR,
        ...forcedFontStyle,
      },
    },
    palette: {
      primary: primaryPalette,
      secondary: secondaryPalette,
      links: linksPalette,
      buttons: {
        ...buttonsPalette,
        light: lighten(BUTTONS_LINKS, 0.7),
      },
      dataVisualizationPrimary: dataVisualizationPrimaryPalette,
      dataVisualizationSecondary: dataVisualizationSecondaryPalette,
      dataVisualizationTertiary: dataVisualizationTertiaryPalette,
      dataVisualizationNegative: dataVisualizationNegativePalette,
      shadows: COMMON_THEME_PROPERTIES?.palette?.shadows ?? THEME_SHADOWS,
      education: secondaryPalette,
      divider: primaryPalette.light,
    },
    logos,
  };
}

export function getThemeFromTenantBranding(
  branding: Branding_BrandedFragment,
  // When true, headers use the tenant- or sub-brand-configured font face
  // (DEFAULT → Romie, ARIAL → Arial) instead of our default heading/body
  // faces. `useCustomerTheme` always passes true (the customer theme honors
  // the brand's face); direct callers (e.g. the login pages) keep the default
  // faces.
  shouldUseCustomFontFace = false
): Theme {
  const { brand, logos } = dataToBrand(branding);
  const themeOptions = makeThemeFromBrand(
    brand,
    logos,
    shouldUseCustomFontFace ? branding.presentationHeaderFontFace : undefined
  );
  return createTheme(COMMON_THEME_PROPERTIES, themeOptions);
}
